import { KatButton } from '@amzn/katal-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAssetContext } from 'src/hooks/useAssetContext';
import { useCampaignMetadata } from 'src/hooks/useCampaignMetadata';
import { AssetStatus, METRIC_NAME } from 'src/types';
import styles from './CampaignCreationSection.module.css';
import { logger } from 'src/logger';
import { metricsPublisher } from 'src/metrics';
import { getControlPlaneClient } from 'src/contexts/ClientContext';
import {
  AssetType,
  CampaignStatus,
  CMS,
  CreateCampaignCommandInput,
  GetCampaignCommandOutput,
} from '@amzn/fire-tv-campaign-automation-service';
import { useSelection } from 'src/contexts/SelectionContext';
import { getUserAlias } from 'src/components/authentication/AuthenticationUtils';
import { useAuthentication } from 'src/contexts/AuthenticationContext';
import { useToast } from 'src/contexts/ToastContext';
import { getErrorMessage } from 'src/utils/ErrorUtils';
import { poll } from 'src/utils/Poller';
import { getAssetInputsFromConfig } from 'src/configs/assetConfig';
import { PollingMaxRetriesError, PollingTimeoutError } from 'src/errors/CustomErrors';
import { getPlacementFeatures } from 'src/configs/placementFeatureConfig';
import { CampaignSuccessModal } from './CampaignSuccessModal';
import { LoadingOverlay } from 'src/components/general/LoadingOverlay';
import {
  getCampaignStatus,
  initiateCampaignCreation,
} from 'src/utils/clients/controlPlane/apiMethods';
import { getMetadataConfig } from 'src/configs/campaignMetadataConfig';
import { useRecentCampaigns } from 'src/hooks/useRecentCampaigns';

// Constants for polling configuration
const POLL_INTERVAL_SECONDS = 15; // Matches the time taken to receive Wharf shipment notification
const POLL_MAX_RETRIES = 10;
const POLL_TIMEOUT_SECONDS = 300;

// Campaign status messages
const CAMPAIGN_CREATION_STATUS_MESSAGES = {
  initiated: 'Campaign creation initiated',
  completed: 'Campaign creation completed',
  error: 'Error creating campaign, please try again in some time',
  pollingRetry: 'Retrying campaign status check...',
  pollingFailed: 'Unable to check campaign status. Please try again later.',
  pollingTimeout:
    'Campaign status check is taking longer than expected. Please refresh to see updates.',
  maxRetriesExceeded:
    'Unable to confirm campaign status after multiple attempts. Please refresh to check status.',
} as const;

// Button text messages
const PUBLISH_BUTTON_MESSAGES = {
  publish: 'Publish Campaign',
  publishing: 'Publishing Campaign...',
} as const;

// Terminal campaign states
// Note: Additional terminal states will be added here as they become available in the model
const TERMINAL_CAMPAIGN_STATES: CampaignStatus[] = [CampaignStatus.CAMPAIGN_CREATION_COMPLETED];

type CampaignState = GetCampaignCommandOutput & {
  error?: string;
};

export const CampaignCreationSection = () => {
  // Get client
  const controlPlaneClient = getControlPlaneClient();
  // Get contexts
  const { assetStates } = useAssetContext();
  const { metadata, updateMetadata } = useCampaignMetadata();
  const { marketplace, placement, deviceFamily, locale } = useSelection();
  const toast = useToast();
  // Get user alias
  const { user } = useAuthentication();
  const userAlias = getUserAlias(user);
  // Recent campaigns
  const { addCampaign } = useRecentCampaigns(marketplace ?? '');
  // Local states
  const [campaignState, setCampaignState] = useState<Partial<CampaignState>>({});
  // isLoading controls the UI state of the publish button
  // It should remain true during the entire campaign creation process including polling
  // and only be set to false when the process completes (success or terminal error)
  const [isLoading, setIsLoading] = useState(false);
  const [shouldPollForCampaign, setShouldPollForCampaign] = useState<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Use refs instead of state for polling to avoid re-renders
  const isPollingActiveRef = useRef<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const pollingCampaignIdRef = useRef<string | null>(null);

  // Helper method to reset polling state
  const resetPollingState = useCallback(() => {
    setIsLoading(false);
    isPollingActiveRef.current = false;
    pollingCampaignIdRef.current = null;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Set state wrapper to preserve existing state
  const updateCampaignState = (newState: Partial<CampaignState>) => {
    setCampaignState((prevState) => ({ ...prevState, ...newState }));
  };

  // Check if all required assets are in COMPLETED state
  const areAssetsComplete = useMemo(() => {
    logger.debug('Checking if assets are complete...', { assetStates });
    const requiredAssetTypes = getAssetInputsFromConfig(deviceFamily, placement) || [];
    return requiredAssetTypes.every((type) => assetStates[type]?.status === AssetStatus.COMPLETED);
  }, [assetStates, deviceFamily, placement]);

  // Check if all required metadata fields are filled
  const isMetadataComplete = useMemo(() => {
    logger.debug('Checking if metadata is complete...', { metadata });

    // Get complete metadata config including device-family-specific fields
    const allMetadataConfig = getMetadataConfig(deviceFamily);

    return (
      Object.entries(allMetadataConfig)
        // Filter config entries to get only required fields, then verify each has a value
        .filter(([_, value]) => value.required)
        .every(([key]) => {
          const metadataValue = metadata[key as keyof typeof metadata];
          return metadataValue !== undefined && metadataValue !== '';
        })
    );
  }, [metadata, deviceFamily]);

  // Check if all required asset types are present
  const areAllAssetTypesPresent = useMemo(() => {
    if (!placement || !deviceFamily) {
      return false;
    }
    return (
      getAssetInputsFromConfig(deviceFamily, placement)?.every((type) => type in assetStates) ||
      false
    );
  }, [assetStates, deviceFamily, placement]);

  // Enable button only if all conditions are met
  const isPublishEnabled = useMemo(() => {
    return (
      areAssetsComplete &&
      isMetadataComplete &&
      !isLoading &&
      !!placement &&
      !!marketplace &&
      !!locale &&
      !!deviceFamily &&
      areAllAssetTypesPresent
    );
  }, [
    areAssetsComplete,
    isMetadataComplete,
    isLoading,
    placement,
    marketplace,
    areAllAssetTypesPresent,
    locale,
    deviceFamily,
  ]);

  const getAssetsFromAssetStates = (): Record<AssetType, string> => {
    const assets = Object.fromEntries(
      Object.entries(assetStates).map(([assetType, assetState]) => [assetType, assetState.key]),
    );

    // Validate that all required asset types are present
    const missingTypes = Object.values(AssetType).filter((type) => !(type in assets));
    if (missingTypes.length > 0) {
      throw new Error(`Missing required asset types: ${missingTypes.join(', ')}`);
    }

    return assets as Record<AssetType, string>;
  };

  const generateCampaignCreationRequest = (): CreateCampaignCommandInput => {
    if (!marketplace) {
      throw new Error('Marketplace is not defined');
    }

    return {
      assets: getAssetsFromAssetStates(),
      // ToDo: Once Rodeo functionality is setup, this will be part of the MetadataContext
      cms: CMS.CONTENT_SYMPHONY,
      actor: userAlias,
      marketPlace: marketplace,
      metadata,
      deviceFamily,
    };
  };

  const getCampaignStatusWrapper = () =>
    getCampaignStatus(controlPlaneClient, campaignState.campaignId);

  const initiatePoller = useCallback(
    async (abortSignal?: AbortSignal) => {
      const pollerOptions = {
        intervalSeconds: POLL_INTERVAL_SECONDS,
        maxRetries: POLL_MAX_RETRIES,
        timeoutSeconds: POLL_TIMEOUT_SECONDS,
        abortSignal,
        successCondition: (result: GetCampaignCommandOutput) => {
          // Check if the current status is a terminal state
          const isTerminalState = TERMINAL_CAMPAIGN_STATES.includes(result.status);

          // For now, we only have CAMPAIGN_CREATION_COMPLETED as a terminal state
          // When additional terminal states are added to the model, this logic will be expanded
          return isTerminalState;
        },
        onError: (error: Error) => {
          logger.error('Error polling campaign status', { error });

          // This callback is only invoked for non-terminal errors that will be retried
          // Terminal errors (timeout, aborted, max retries) bypass this callback
          // and are caught in the outer catch block

          // Update state to reflect the error
          updateCampaignState({ error: error.message });

          // Notify user of the retry
          // toast.addToast({
          //   variant: 'warning',
          //   description: CAMPAIGN_CREATION_STATUS_MESSAGES.pollingRetry,
          // });
        },
        onSuccess: (result: GetCampaignCommandOutput) => {
          logger.debug('Campaign creation completed', { result });

          if (result.status === CampaignStatus.CAMPAIGN_CREATION_COMPLETED) {
            updateCampaignState({
              status: result.status,
              campaignCmsUrl: result.campaignCmsUrl,
              lastUpdatedTime: result.lastUpdatedTime,
            });
            toast.addToast({
              variant: 'success',
              description: CAMPAIGN_CREATION_STATUS_MESSAGES.completed,
            });
            setShowSuccessModal(true);
            if (result.campaignCmsUrl) {
              addCampaign(result.campaignCmsUrl, metadata.title ?? '');
            }
          } else if (result.status === CampaignStatus.CAMPAIGN_CREATION_FAILED) {
            // Emit campaign creation error metric for failed status
            metricsPublisher.emitCount(METRIC_NAME.CAMPAIGN_CREATION_ERROR);

            updateCampaignState({
              status: result.status,
              error: CAMPAIGN_CREATION_STATUS_MESSAGES.error,
            });
            toast.addToast({
              variant: 'danger',
              description: CAMPAIGN_CREATION_STATUS_MESSAGES.error,
              persistent: true,
            });
          }

          resetPollingState();
        },
      };

      logger.debug('Polling campaign status...', { pollerOptions });

      try {
        await poll(getCampaignStatusWrapper, pollerOptions);
      } catch (error) {
        // This catch block handles all terminal errors that stop the polling process
        logger.error('Terminal error during polling - polling has stopped', { error });

        // Always reset for terminal errors
        resetPollingState();

        // Use type checking for specific error types with simplified messages
        let description: string;
        if (error instanceof PollingTimeoutError) {
          description = CAMPAIGN_CREATION_STATUS_MESSAGES.pollingTimeout;
        } else if (error instanceof PollingMaxRetriesError) {
          description = CAMPAIGN_CREATION_STATUS_MESSAGES.maxRetriesExceeded;
        } else {
          // Handles both PollingAbortedError and other generic errors
          description = CAMPAIGN_CREATION_STATUS_MESSAGES.pollingFailed;
        }

        // Emit campaign creation error metric for any polling error
        metricsPublisher.emitCount(METRIC_NAME.CAMPAIGN_CREATION_ERROR);

        updateCampaignState({ error: getErrorMessage(error) });
        toast.addToast({
          variant: 'danger',
          description,
          persistent: true,
        });
      }
    },
    [
      getCampaignStatus,
      updateCampaignState,
      setShowSuccessModal,
      toast,
      resetPollingState,
      addCampaign,
    ],
  );

  const handlePublish = async () => {
    if (isLoading) return; // Prevent multiple clicks during loading

    logger.debug('Publishing campaign...', {
      assets: assetStates,
      metadata,
    });
    setIsLoading(true);
    try {
      const response = await initiateCampaignCreation(
        controlPlaneClient,
        generateCampaignCreationRequest(),
      );
      logger.info('Campaign creation initiated', { response });
      updateCampaignState({ campaignId: response.campaignId });
      toast.addToast({
        variant: 'info',
        description: CAMPAIGN_CREATION_STATUS_MESSAGES.initiated,
      });
      setShouldPollForCampaign(true);
    } catch (error) {
      logger.error('Error publishing campaign', { error });

      // Emit campaign creation error metric
      metricsPublisher.emitCount(METRIC_NAME.CAMPAIGN_CREATION_ERROR);

      updateCampaignState({ error: getErrorMessage(error) });
      toast.addToast({
        variant: 'danger',
        description: CAMPAIGN_CREATION_STATUS_MESSAGES.error,
      });
      resetPollingState();
    }
  };

  const handleCloseSuccessModal = () => setShowSuccessModal(false);

  // Memoize polling function with all its dependencies to prevent render loops
  const startPolling = useCallback(() => {
    if (!campaignState.campaignId || isPollingActiveRef.current) {
      logger.debug('Polling skipped - no campaign ID or already polling', {
        campaignId: campaignState.campaignId,
        isPollingActive: isPollingActiveRef.current,
      });
      return;
    }

    logger.debug('Starting campaign status polling', {
      campaignId: campaignState.campaignId,
    });

    // Mark polling as active and store campaign ID
    isPollingActiveRef.current = true;
    pollingCampaignIdRef.current = campaignState.campaignId;

    // Create a new abort controller
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // Abort any existing polling
    }
    abortControllerRef.current = new AbortController();

    // Polling as an async IIFE
    (async () => {
      try {
        await initiatePoller(abortControllerRef.current?.signal);
      } catch (error) {
        logger.error('Error in campaign status polling', { error });

        // We don't need to handle specific error types here as they're handled in initiatePoller
        // This catch block only handles errors that might occur outside the polling mechanism
        updateCampaignState({ error: getErrorMessage(error) });
        toast.addToast({
          variant: 'danger',
          description: CAMPAIGN_CREATION_STATUS_MESSAGES.pollingFailed,
          persistent: true,
        });
      } finally {
        // Reset states
        setShouldPollForCampaign(false);
      }
    })();
  }, [campaignState.campaignId, initiatePoller, toast, updateCampaignState]);

  // Watch for shouldPollForCampaign changes and start polling when needed
  // Only trigger when shouldPollForCampaign transitions to true or campaignId changes
  useEffect(() => {
    if (shouldPollForCampaign && campaignState.campaignId) {
      startPolling();
    }
  }, [shouldPollForCampaign, campaignState.campaignId, startPolling]);

  // Update metadata when locale changes
  useEffect(() => {
    if (locale) {
      updateMetadata({ locale });
    }
  }, [locale, updateMetadata]);

  // Cleanup effect - only runs on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        logger.debug('Cleaning up polling on component unmount');
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  if (!placement) {
    return null;
  }

  const placementFeatures = getPlacementFeatures(placement);

  if (!placementFeatures.showPublishSection) {
    return null;
  }

  return (
    <div className={styles.container}>
      {isLoading && <LoadingOverlay fullScreen text={PUBLISH_BUTTON_MESSAGES.publishing} />}
      <div className={styles.buttonContainer}>
        <KatButton
          variant="primary"
          disabled={!isPublishEnabled}
          onClick={handlePublish}
          data-testid="publish-button"
        >
          {isLoading ? PUBLISH_BUTTON_MESSAGES.publishing : PUBLISH_BUTTON_MESSAGES.publish}
        </KatButton>
      </div>

      {showSuccessModal && campaignState.campaignCmsUrl && (
        <CampaignSuccessModal
          isVisible={showSuccessModal}
          campaignCmsUrl={campaignState.campaignCmsUrl}
          onClose={handleCloseSuccessModal}
        />
      )}
    </div>
  );
};
